/**
 * ProductionCaretSystem - caret positioning using WASM caret_map ONLY
 * 
 * ✅ V8: Use caret_map directly - no recalculation, no rounding errors
 * ✅ MANDATORY: All caret positioning from WASM - no Canvas math
 */

import type { ParagraphSnapshot } from '../../../core/types/Typesetter';
import { InputLogger } from '../../../../../utils/loggers/InputLogger';

// Debug flag - set to false for production
const DEBUG_ENABLED = false; // ✅ DISABLED: Rapid typing performance fix
const debugLog = DEBUG_ENABLED ? console.log.bind(console) : () => {};

// 🔬 CARET DIAGNOSTICS: DISABLED - consolidated in ClickEventCollector
const CARET_DIAG_ENABLED = false; // ✅ DISABLED: Click logging consolidated

export type CaretPixelPosition = { x: number; y: number; height: number };

export class ProductionCaretSystem {
  /**
   * ✅ V8: Get caret position using WASM caret_map DIRECTLY
   * NO recalculation, NO Canvas math, ONLY authoritative WASM data
   */
  static getCaretPosition(
    snapshot: ParagraphSnapshot,
    cursorGraphemeIndex: number,
    topMarginPx: number
  ): CaretPixelPosition {
    // ✅ CRITICAL DIAGNOSTIC: Check if caret_map is empty (primary cause of cursor stuck at 0)
    const caretMap = snapshot?.caret_map || [];
    
    // 🔬 TEMPORARY DIAGNOSTIC: Log caret lookup details
    if (CARET_DIAG_ENABLED) {
      const lineRanges = snapshot?.lines?.map((l, i) => `L${i}:[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', ') || 'none';
      const caretIndices = caretMap.map(c => c.grapheme_index).join(',');
      
      // 🔬 DIAGNOSTIC: Analyze caret map gaps
      const hasExactMatch = caretMap.some(c => c.grapheme_index === cursorGraphemeIndex);
      if (!hasExactMatch && caretMap.length > 0) {
        // Find nearest positions
        let nearestBefore: number | null = null;
        let nearestAfter: number | null = null;
        for (const c of caretMap) {
          if (c.grapheme_index < cursorGraphemeIndex) {
            if (nearestBefore === null || c.grapheme_index > nearestBefore) {
              nearestBefore = c.grapheme_index;
            }
          } else if (c.grapheme_index > cursorGraphemeIndex) {
            if (nearestAfter === null || c.grapheme_index < nearestAfter) {
              nearestAfter = c.grapheme_index;
            }
          }
        }
        InputLogger.caretMapGaps.cursorInGap(
          cursorGraphemeIndex,
          nearestBefore,
          nearestAfter,
          `lines: ${lineRanges}`
        );
      }
      
      InputLogger.caretDiag.getPosition(
        cursorGraphemeIndex,
        caretMap.length,
        caretIndices,
        lineRanges,
        undefined,
        { hasExactMatch }
      );
    }
    
    DEBUG_ENABLED && console.log('[Caret]', { 
      caretMapLen: caretMap.length, 
      cursorGraphemeIndex,
      linesCount: snapshot?.lines?.length || 0,
      hasSnapshot: !!snapshot,
      hasCaretMap: !!snapshot?.caret_map,
      caretMapType: Array.isArray(caretMap) ? 'array' : typeof caretMap
    });

    debugLog('🎯 [CARET] getCaretPosition called:', {
      cursorGraphemeIndex,
      caretMapLength: caretMap.length,
      linesCount: snapshot?.lines?.length || 0,
      topMarginPx,
      lines: snapshot?.lines?.slice(0, 5).map(line => ({
        graphemeRange: `[${line.start_grapheme_index} → ${line.end_grapheme_index})`,
        isEmpty: line.start_grapheme_index === line.end_grapheme_index,
        xOffset: (line.x_offset_fp / 64).toFixed(2),
        height: (line.height_fp / 64).toFixed(2),
        runs: line.runs.length
      })),
      caretsNearCursor: caretMap.filter(c => 
        Math.abs(c.grapheme_index - cursorGraphemeIndex) <= 3
      ).map(c => ({
        grapheme: c.grapheme_index,
        x: (c.x_fp / 64).toFixed(2),
        y: (c.y_fp / 64).toFixed(2),
        isRTL: c.is_rtl
      }))
    });

    if (!snapshot?.lines?.length) {
      return { x: topMarginPx, y: topMarginPx, height: 16 };
    }
    
    if (caretMap.length === 0) {
      // 🚨 CRITICAL DIAGNOSTIC: Empty caret_map is the primary cause of cursor stuck at position 0
      console.error('[Caret] ❌ CARET_MAP IS EMPTY!', {
        caretMapLen: caretMap.length,
        cursorGraphemeIndex,
        linesCount: snapshot.lines?.length || 0,
        hasSnapshot: !!snapshot,
        hasCaretMapProperty: 'caret_map' in (snapshot || {}),
        snapshotKeys: snapshot ? Object.keys(snapshot) : [],
        firstLineX: snapshot.lines?.[0]?.x_offset_fp ? (snapshot.lines[0].x_offset_fp / 64.0) : 'N/A',
        note: 'Caret will be stuck at left edge (firstLine.x_offset_fp) - this explains cursor not advancing!'
      });
      debugLog('[CaretDebug] caret_map is EMPTY! Using fallback.');
      const firstLine = snapshot.lines[0];
      // 🔧 FIX (2026-01-20): Use font metrics for caret height (matches MS Word)
      return {
        x: firstLine.x_offset_fp / 64.0,
        y: topMarginPx,
        height: (firstLine.ascent_fp + firstLine.descent_fp) / 64.0
      };
    }

    // Find which line this grapheme is on; Y should come from WASM baseline/ascent
    let accumulatedY = topMarginPx; // kept for fallback only; pagination passes 0

    // 🔥 FIX (2026-01-12): Track best matching line for gap handling
    // When cursor falls in a gap between lines (e.g., on a newline character),
    // we need to find the nearest line rather than falling through to fallback.
    // 🔥 FIX (2026-01-12 v2): Track both "before" and "after" lines for gaps.
    // For Enter key behavior, we prefer the NEXT line (starts after cursor).
    let bestMatchLineBefore: typeof snapshot.lines[0] | null = null;
    let bestMatchLineAfter: typeof snapshot.lines[0] | null = null;
    let bestDistanceBefore = Infinity;
    let bestDistanceAfter = Infinity;
    // Keep overall bestMatchLine for backward compatibility
    let bestMatchLine: typeof snapshot.lines[0] | null = null;
    let bestMatchDistance = Infinity;

    for (let lineIdx = 0; lineIdx < snapshot.lines.length; lineIdx++) {
      const line = snapshot.lines[lineIdx];
      // ✅ Use WASM-provided grapheme indices directly (no manual counting!)
      const lineStart = line.start_grapheme_index;
      const lineEnd = line.end_grapheme_index;

      // ✅ CRITICAL FIX: Line ranges are [start, end) - EXCLUSIVE end!
      // Special case: Empty lines where start==end still contain that cursor position
      const isEmpty = lineStart === lineEnd;
      
      // 🔥 FIX (2026-01-15): When cursor equals both:
      //   - END of this line (lineEnd) 
      //   - START of the next line (nextLine.start_grapheme_index)
      // ...prefer the NEXT line. This handles ENTER key behavior where cursor
      // should appear at start of the new line, not end of the previous line.
      //
      // Example: After ENTER at "x|": L0:[0-2), L1:[2-21), cursor=2
      // Cursor 2 equals end of L0 AND start of L1 → prefer L1 (the new line)
      const nextLine = snapshot.lines[lineIdx + 1];
      const nextLineStartsAtCursor = nextLine && nextLine.start_grapheme_index === cursorGraphemeIndex;
      
      const isOnThisLine = isEmpty 
        ? cursorGraphemeIndex === lineStart
        : nextLineStartsAtCursor
          ? cursorGraphemeIndex >= lineStart && cursorGraphemeIndex < lineEnd  // EXCLUDE end when next line starts here
          : cursorGraphemeIndex >= lineStart && cursorGraphemeIndex <= lineEnd;  // [start, end] INCLUSIVE end

      // 🔥 FIX: Track distance to this line for gap handling
      // Gaps occur between lines (e.g., cursor at 42 when lines are [0-41) and [43-43))
      // The newline characters fall in these gaps.
      if (!isOnThisLine) {
        // 🔥 FIX (2026-01-12 v2): Track before/after separately for Enter key behavior
        if (cursorGraphemeIndex < lineStart) {
          // This line STARTS AFTER cursor - candidate for "next line" (Enter behavior)
          const distance = lineStart - cursorGraphemeIndex;
          if (distance < bestDistanceAfter) {
            bestDistanceAfter = distance;
            bestMatchLineAfter = line;
          }
          if (distance < bestMatchDistance) {
            bestMatchDistance = distance;
            bestMatchLine = line;
          }
        } else if (cursorGraphemeIndex > lineEnd) {
          // This line ENDS BEFORE cursor - candidate for "previous line"
          const distance = cursorGraphemeIndex - lineEnd;
          if (distance < bestDistanceBefore) {
            bestDistanceBefore = distance;
            bestMatchLineBefore = line;
          }
          if (distance < bestMatchDistance) {
            bestMatchDistance = distance;
            bestMatchLine = line;
          }
        }
      }

      if (isOnThisLine) {
        // Found the line!
        
        // ✅ ROBUST: Find closest caret position if exact match doesn't exist
        let caretEntry = caretMap.find(c => c.grapheme_index === cursorGraphemeIndex);
        
        if (!caretEntry) {
          // No exact match - find closest caret on this line
          debugLog(`[CaretDebug] No exact match for grapheme ${cursorGraphemeIndex}, finding closest on line [${lineStart}, ${lineEnd})`);
          let closestDist = Infinity;
          for (const c of caretMap) {
            // ✅ FIX (2026-01-11): Include end-of-line caret position (grapheme_index === lineEnd)
            // WASM line ranges are [start, end) exclusive, but the caret_map includes a position
            // AT lineEnd for the cursor after the last character. Without this, typing at end-of-line
            // causes caret to stay at position before the last character.
            // Example: Line [3, 4) with text "b" has carets at 3 (before 'b') and 4 (after 'b').
            //          Cursor at 4 should find caret at 4, not fall back to caret at 3.
            const isInRange = isEmpty
              ? c.grapheme_index === lineStart
              : c.grapheme_index >= lineStart && c.grapheme_index <= lineEnd;
            
            if (isInRange) {
              const dist = Math.abs(c.grapheme_index - cursorGraphemeIndex);
              if (dist < closestDist) {
                closestDist = dist;
                caretEntry = c;
              }
            }
          }
          if (caretEntry) {
            debugLog(`[CaretDebug] Using closest: grapheme ${caretEntry.grapheme_index}, x=${(caretEntry.x_fp/64).toFixed(2)}px`);
          } else {
            debugLog(`[CaretDebug] NO CARET FOUND on line! Falling back to line start`);
          }
        }
        
        // ✅ CRITICAL FIX: caret_map x_fp values are ALREADY ABSOLUTE (include line.x_offset_fp)
        // Don't add lineX again - it would double-count the offset!
        const absoluteX = caretEntry ? (caretEntry.x_fp / 64.0) : (line.x_offset_fp / 64.0);
        
        // ✅ Use WASM-provided baseline/ascent to compute visual top of line
        const baselineY = (line.baseline_y_fp || 0) / 64.0;
        const ascent = (line.ascent_fp || 0) / 64.0;
        const lineTopY = baselineY - ascent;

        // 🔧 FIX (2026-01-20): Use font metrics (ascent + descent) for caret height, NOT line height.
        // Line height includes line spacing, which makes caret grow/shrink when line spacing changes.
        // MS Word behavior: caret height = font metrics only, independent of line spacing.
        const caretHeight = (line.ascent_fp + line.descent_fp) / 64.0;

        const result = {
          x: absoluteX,
          y: lineTopY,
          height: caretHeight
        };
        
        // 🔬 TEMPORARY DIAGNOSTIC: Log result
        if (CARET_DIAG_ENABLED) {
          InputLogger.caretDiag.positionResult(
            cursorGraphemeIndex,
            `[${lineStart}-${lineEnd})`,
            caretEntry?.grapheme_index ?? null,
            result.x,
            result.y,
            result.height
          );
        }
        
        debugLog('[CaretDebug] Found caret position:', {
          graphemeIndex: cursorGraphemeIndex,
          caretEntry: caretEntry ? {
            x_fp: caretEntry.x_fp,
            y_fp: caretEntry.y_fp,
            x_px: caretEntry.x_fp / 64,
            y_px: caretEntry.y_fp / 64
          } : null,
          lineRange: `[${lineStart}→${lineEnd}]`,
          absoluteX,
          accumulatedY,
          result
        });
        
        return result;
      }

      accumulatedY += line.height_fp / 64.0;
    }

    // Beyond last line: cursor is ahead of WASM (typing fast)
    // ✅ OPTIMISTIC POSITIONING: Estimate caret position based on last known position
    const lastLine = snapshot.lines[snapshot.lines.length - 1];
    const lastCaret = caretMap[caretMap.length - 1];
    
    if (lastCaret && cursorGraphemeIndex > lastCaret.grapheme_index) {
      // Cursor is ahead - estimate position by extending from last caret
      const charsAhead = cursorGraphemeIndex - lastCaret.grapheme_index;
      const avgCharWidth = 8.39; // Approximate width for typical text
      const estimatedX = (lastCaret.x_fp / 64.0) + (charsAhead * avgCharWidth);
      
      debugLog('[CaretDebug] Cursor ahead of WASM - using optimistic positioning:', {
        cursorIndex: cursorGraphemeIndex,
        lastCaretIndex: lastCaret.grapheme_index,
        charsAhead,
        estimatedX
      });
      
      const baselineY = (lastLine.baseline_y_fp || 0) / 64.0;
      const ascent = (lastLine.ascent_fp || 0) / 64.0;
      const lineTopY = baselineY - ascent;
      
      // 🔧 FIX (2026-01-20): Use font metrics for caret height (matches MS Word)
      return {
        x: estimatedX,
        y: lineTopY,
        height: (lastLine.ascent_fp + lastLine.descent_fp) / 64.0
      };
    }
    
    // 🔥 FIX (2026-01-12): Use bestMatchLine if cursor fell in a gap between lines
    // This handles the case where cursor is on a newline character (e.g., position 42
    // when lines are [0-41) and [43-43)). Position at end of the nearest line.
    // 🔥 FIX (2026-01-12 v2): Increased tolerance from 2 to 15 to handle multi-paragraph
    // gaps where Enter key creates larger grapheme index gaps between paragraph lines.
    // Example: cursor=161 with lines [157) and [167) has distance=4.
    // 🔥 FIX (2026-01-12 v3): Prefer the NEXT line (after cursor) for Enter key behavior.
    // When user presses Enter, cursor should appear at START of new line, not END of previous.
    const gapLine = bestMatchLineAfter || bestMatchLine; // Prefer "after" line
    const gapDistance = bestMatchLineAfter ? bestDistanceAfter : bestMatchDistance;
    
    if (gapLine && gapDistance <= 15) {
      const lineStart = gapLine.start_grapheme_index;
      const lineEnd = gapLine.end_grapheme_index;
      
      // Find the closest caret entry to cursor within this line's range
      let closestCaret = caretMap.find(c => c.grapheme_index === cursorGraphemeIndex);
      if (!closestCaret) {
        // Find closest caret in or near this line
        let minDist = Infinity;
        for (const c of caretMap) {
          const dist = Math.abs(c.grapheme_index - cursorGraphemeIndex);
          if (dist < minDist) {
            minDist = dist;
            closestCaret = c;
          }
        }
      }
      
      if (CARET_DIAG_ENABLED) {
        InputLogger.caretDiag.gapFix(
          cursorGraphemeIndex,
          `[${lineStart}-${lineEnd})`,
          gapDistance,
          !!bestMatchLineAfter
        );
      }
      
      const baselineY = (gapLine.baseline_y_fp || 0) / 64.0;
      const ascent = (gapLine.ascent_fp || 0) / 64.0;
      const lineTopY = baselineY - ascent;
      
      // 🔥 FIX: For "after" line, position cursor at START of line (left margin)
      // For "before" line, position cursor at END of line
      const absoluteX = bestMatchLineAfter
        ? (gapLine.x_offset_fp / 64.0)  // Start of next line (left margin)
        : (closestCaret 
            ? (closestCaret.x_fp / 64.0) 
            : ((gapLine.x_offset_fp + (gapLine.width_fp || 0)) / 64.0)); // End of prev line
      
      // 🔧 FIX (2026-01-20): Use font metrics for caret height (matches MS Word)
      return {
        x: absoluteX,
        y: lineTopY,
        height: (gapLine.ascent_fp + gapLine.descent_fp) / 64.0
      };
    }
    
    // Fallback: snap to last caret position
    // 🔬 DIAGNOSTIC: This fallback means cursor didn't match any line!
    if (CARET_DIAG_ENABLED) {
      const lineRanges = snapshot?.lines?.map((l, i) => `L${i}:[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', ') || 'none';
      console.warn(`⚠️ [CARET_DIAG] FALLBACK: cursor=${cursorGraphemeIndex} didn't match any line!`, {
        lineRanges,
        lastLineHeight: (lastLine.height_fp / 64.0).toFixed(2),
        bestMatchDistance
      });
    }
    
    // ✅ CRITICAL FIX: caret_map x_fp values are ALREADY ABSOLUTE
    const absoluteX = lastCaret ? (lastCaret.x_fp / 64.0) : (lastLine.x_offset_fp / 64.0);
    const baselineY = (lastLine.baseline_y_fp || 0) / 64.0;
    const ascent = (lastLine.ascent_fp || 0) / 64.0;
    const lineTopY = baselineY - ascent;
    
    // 🔧 FIX (2026-01-20): Use font metrics for caret height (matches MS Word)
    return {
      x: absoluteX,
      y: lineTopY,
      height: (lastLine.ascent_fp + lastLine.descent_fp) / 64.0
    };
  }


  /**
   * ✅ V8: Find grapheme index from click using caret_map DIRECTLY  
   * NO recalculation, ONLY authoritative WASM caret positions (X only, Y from lines)
   */
  static getGraphemeIndexFromClick(
    snapshot: ParagraphSnapshot,
    clickX: number,
    clickY: number,
    topMargin: number
  ): number {
    // DEBUG: Log call stack to identify what's calling this repeatedly
    if (Math.random() < 0.01) { // Only log 1% of calls to avoid spam
      DEBUG_ENABLED && console.log('[getGraphemeIndexFromClick] Called from:', new Error().stack?.split('\n').slice(2, 5).join('\n'));
    }
    
    const { lines, caret_map: caretMap } = snapshot;

    if (!lines?.length) {
      return 0;
    }

    // ✅ OPTIMIZED APPROACH: Use caret_map entries directly (they have x_fp, y_fp)
    // This avoids calling getCaretPosition() for every caret!
    
    // Step 1: Find which WASM line was clicked by Y coordinate (use baseline/ascent)
    let targetLineStart = -1;
    let targetLineEnd = -1;
    let targetLineY = 0;
    let targetLineIndex = -1;

    const EPS = 1.0; // small tolerance for Y hit testing in CSS-scaled canvases
    for (let i = 0; i < snapshot.lines.length; i++) {
      const line = snapshot.lines[i];
      const lineHeight = line.height_fp / 64.0;
      const baselineY = (line.baseline_y_fp || 0) / 64.0;
      const ascent = (line.ascent_fp || 0) / 64.0;
      const lineTopY = baselineY - ascent;

      // Check if click is within this line's Y range (with small epsilon)
      if (clickY >= lineTopY - EPS && clickY < lineTopY + lineHeight + EPS) {
        targetLineStart = line.start_grapheme_index;
        targetLineEnd = line.end_grapheme_index;
        targetLineY = lineTopY;
        targetLineIndex = i;
        break;
      }
    }

    // If no line found by strict range, pick the visually closest line by Y
    if (targetLineStart === -1) {
      let best = { dist: Infinity, start: 0, end: 0, y: 0 };
      for (const line of snapshot.lines) {
        const baselineY = (line.baseline_y_fp || 0) / 64.0;
        const ascent = (line.ascent_fp || 0) / 64.0;
        const lineTopY = baselineY - ascent;
        const lineHeight = line.height_fp / 64.0;
        const centerY = lineTopY + lineHeight / 2;
        const d = Math.abs(clickY - centerY);
        if (d < best.dist) {
          best = { dist: d, start: line.start_grapheme_index, end: line.end_grapheme_index, y: lineTopY };
        }
      }
      targetLineStart = best.start;
      targetLineEnd = best.end;
      targetLineY = best.y;
    }

    // Step 2: Find closest caret within the line's grapheme range
    // ✅ Use caret_map entries directly; prefer binary search within the line subrange for LTR
    const isEmpty = targetLineStart === targetLineEnd;
    if (isEmpty) {
      return targetLineStart;
    }

    // Helper: lower_bound by grapheme_index
    const lowerBoundByGrapheme = (arr: any[], target: number): number => {
      let lo = 0, hi = arr.length;
      while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if ((arr[mid]?.grapheme_index ?? 0) < target) lo = mid + 1; else hi = mid;
      }
      return lo;
    };

    const startIdx = lowerBoundByGrapheme(caretMap, targetLineStart);
    const endAtOrAfter = lowerBoundByGrapheme(caretMap, targetLineEnd);
    // Make terminal caret inclusive when present
    let endInclusive = endAtOrAfter - 1;
    if (endAtOrAfter < caretMap.length && (caretMap[endAtOrAfter]?.grapheme_index === targetLineEnd)) {
      endInclusive = endAtOrAfter;
    }
    
    // Diagnostic logging disabled to reduce console noise during selection drag
    // Uncomment for debugging click-to-position issues:
    // DEBUG_ENABLED && console.log('🔍 [getGraphemeIndexFromClick] Line Detection:', {
    //   clickX: clickX.toFixed(2),
    //   clickY: clickY.toFixed(2),
    //   lineIndex: targetLineIndex,
    //   lineRange: `[${targetLineStart}, ${targetLineEnd})`,
    //   lineY: targetLineY.toFixed(2),
    //   caretRangeIndices: `[${startIdx}, ${endInclusive}]`,
    //   caretsInRange: endInclusive - startIdx + 1
    // });

    // Basic unit diagnostics for range integrity
    try {
      console.assert(endInclusive >= startIdx - 1, '[CaretDebug] Invalid caret range for line');
    } catch {}

    const count = Math.max(0, endInclusive - startIdx + 1);
    if (count <= 0) {
      return targetLineStart;
    }

    // Detect if any RTL caret in range; if so, fallback to linear scan (x may not be monotonic)
    let hasRTL = false;
    for (let i = startIdx; i <= endInclusive; i++) {
      if (caretMap[i]?.is_rtl) { hasRTL = true; break; }
    }

    let closestIndex = targetLineStart;
    if (hasRTL || count <= 64) {
      // Linear scan for small ranges or RTL
      let bestDist = Infinity;
      let bestCaretX = 0;
      for (let i = startIdx; i <= endInclusive; i++) {
        const cx = (caretMap[i].x_fp || 0) / 64.0;
        const d = Math.abs(cx - clickX);
        if (d < bestDist) { 
          bestDist = d; 
          closestIndex = caretMap[i].grapheme_index;
          bestCaretX = cx;
        }
      }
      // Diagnostic logging disabled to reduce console noise
      // debugLog('Linear scan result:', { closestIndex, bestCaretX, bestDist });
    } else {
      // Binary search by x (assumes non-decreasing x across LTR line)
      let lo = startIdx, hi = endInclusive;
      while (lo <= hi) {
        const mid = (lo + hi) >>> 1;
        const midX = (caretMap[mid].x_fp || 0) / 64.0;
        if (midX < clickX) lo = mid + 1; else hi = mid - 1;
      }
      // lo is the first caret with x >= clickX; compare lo and lo-1
      const candA = Math.min(endInclusive, Math.max(startIdx, lo));
      const candB = Math.min(endInclusive, Math.max(startIdx, lo - 1));
      const ax = (caretMap[candA].x_fp || 0) / 64.0;
      const bx = (caretMap[candB].x_fp || 0) / 64.0;
      const ad = Math.abs(ax - clickX);
      const bd = Math.abs(bx - clickX);
      closestIndex = (ad <= bd ? caretMap[candA] : caretMap[candB]).grapheme_index;
      
      // Diagnostic logging disabled to reduce console noise
      // debugLog('Binary search result:', { closestIndex, candA, candB });
    }

    // If the click is clearly to the right of the last caret in the line, snap to terminal caret
    // ✅ FIX: Use the last CHARACTER caret, not the terminal caret position
    // Terminal caret (at line end) has X at start of next line, which breaks this logic
    let lastCharacterIdx = endInclusive;
    
    // If endInclusive points to the terminal caret (grapheme == targetLineEnd), go back one
    if (lastCharacterIdx >= 0 && caretMap[lastCharacterIdx].grapheme_index === targetLineEnd) {
      lastCharacterIdx--;
      // Diagnostic logging disabled to reduce console noise
      // DEBUG_ENABLED && console.log('🔍 [getGraphemeIndexFromClick] Terminal snap: Excluding terminal caret, using last character instead');
    }
    
    if (lastCharacterIdx >= startIdx && targetLineIndex >= 0) {
      const lastX = (caretMap[lastCharacterIdx].x_fp || 0) / 64.0;
      
      // ✅ UX IMPROVEMENT: Use line box boundaries to determine snap zone
      // If click is to the right of last character but still within the line box,
      // snap to end-of-line. This matches MS Word/Google Docs behavior.
      const line = snapshot.lines[targetLineIndex];
      const lineBoxLeft = (line.x_offset_fp || 0) / 64.0;
      const lineBoxRight = lineBoxLeft + ((line.width_fp || 0) / 64.0);
      
      // Snap if click is between last character and line box right edge
      if (clickX > lastX && clickX <= lineBoxRight) {
        const beforeSnap = closestIndex;
        // ✅ FIX: Snap to the last CHARACTER on this line, NOT targetLineEnd
        // targetLineEnd is the EXCLUSIVE end boundary (start of next line)
        // Line range is [start, end) so the last grapheme on THIS line is targetLineEnd - 1
        // This is true regardless of whether the line ends with newline or not
        closestIndex = targetLineEnd - 1;
        
        // 🔬 DIAGNOSTIC: Log terminal caret snap
        DEBUG_ENABLED && console.log('🔍 [getGraphemeIndexFromClick] Terminal Caret Snap (Line Box):', {
          lastCharacterX: lastX.toFixed(2),
          lastCharacterGrapheme: caretMap[lastCharacterIdx].grapheme_index,
          clickX: clickX.toFixed(2),
          lineBoxLeft: lineBoxLeft.toFixed(2),
          lineBoxRight: lineBoxRight.toFixed(2),
          distanceFromLastChar: (clickX - lastX).toFixed(2),
          endsWithNewline: line.ends_with_newline,
          targetLineEnd,
          targetLineEndMinus1: targetLineEnd - 1,
          snappedFrom: beforeSnap,
          snappedTo: closestIndex
        });
      }
    }

    // Clamp within line bounds defensively
    if (closestIndex < targetLineStart) closestIndex = targetLineStart;
    if (closestIndex > targetLineEnd) closestIndex = targetLineEnd;

    const result = {
      clickX: clickX.toFixed(2),
      clickY: clickY.toFixed(2),
      targetLineY: targetLineY.toFixed(2),
      lineRange: `[${targetLineStart}→${targetLineEnd}]`,
      closestGraphemeIndex: closestIndex,
      closestDistance: 'n/a',
      caretMapLength: caretMap.length,
      caretsInRange: caretMap.filter(c => {
        const isEmpty = targetLineStart === targetLineEnd;
        return isEmpty ? c.grapheme_index === targetLineStart : 
               c.grapheme_index >= targetLineStart && c.grapheme_index <= targetLineEnd;
      }).length,
      terminalCaretPresent: caretMap.some(c => c.grapheme_index === targetLineEnd)
    };
    
    debugLog('🖱️ [getGraphemeIndexFromClick] Result:', result);

    return closestIndex;
  }
}


